<script lang="ts">
	import ModernNavBar from './ModernNavBar.svelte';
	import type { Market } from '$lib/types/market';
	import type { User } from '$lib/types/user';
	import type { MarketAppearance } from '$lib/types/marketAppearance';
	import { onMount } from 'svelte';

	let {
		market = null,
		user = null,
		loggedIn = false,
		showHelpButton = true,
		onHelpClick,
		helpContent,
		onTourClick,
	} = $props<{
		market?: Market | null;
		user?: User | null;
		loggedIn?: boolean;
		showHelpButton?: boolean;
		onHelpClick?: () => void;
		helpContent?: string;
		onTourClick?: () => void;
	}>();

	let appearance = $state<MarketAppearance | null>(null);
	let headerElementOrder = $state(['nav', 'banner', 'slogan']);

	// Load appearance settings
	onMount(() => {
		// Load appearance settings asynchronously
		if (market?.id) {
			(async () => {
				try {
					const response = await fetch(`/api/markets/${market.id}/appearance/public`);
					if (response.ok) {
						const data = await response.json();
						if (data.appearance) {
							appearance = data.appearance;
							// Parse header element order
							if (appearance?.headerElementOrder) {
								try {
									headerElementOrder = JSON.parse(appearance.headerElementOrder);
								} catch {
									// Use default if parsing fails
								}
							}
						}
					}
				} catch (error) {
					console.error('Failed to load appearance settings:', error);
				}
			})();
		}
	});

	// Get the header banner URL if it exists
	const headerBannerUrl = $derived(
		appearance?.headerBannerFileName || market?.headerBannerFileName
			? `/market/header-banner/${market?.id}`
			: null
	);

	// Banner position is determined by the headerElementOrder array

	// Get banner filename - prefer appearance setting, fallback to market setting
	const bannerFileName = $derived(appearance?.headerBannerFileName || market?.headerBannerFileName);
	const bannerAltText = $derived(appearance?.headerBannerAltText || `${market?.name} banner`);

	// Slogan styles
	const sloganStyles = $derived(() => {
		if (!appearance) return '';

		const styles = [];
		if (appearance.sloganFontFamily) styles.push(`font-family: ${appearance.sloganFontFamily}`);
		if (appearance.sloganFontSize) styles.push(`font-size: ${appearance.sloganFontSize}`);
		if (appearance.sloganColor) styles.push(`color: ${appearance.sloganColor}`);

		return styles.join('; ');
	});
</script>

<header class="modern-header">
	{#each headerElementOrder as element (element)}
		{#if element === 'nav'}
			<ModernNavBar
				{market}
				{user}
				{loggedIn}
				{showHelpButton}
				{onHelpClick}
				{helpContent}
				{onTourClick}
			/>
		{:else if element === 'banner' && headerBannerUrl && bannerFileName}
			<div class="header-banner-container">
				<picture>
					<source media="(min-width: 1280px)" srcset="{headerBannerUrl}/desktop/{bannerFileName}" />
					<source media="(min-width: 768px)" srcset="{headerBannerUrl}/laptop/{bannerFileName}" />
					<source media="(min-width: 480px)" srcset="{headerBannerUrl}/tablet/{bannerFileName}" />
					<img
						src="{headerBannerUrl}/mobile/{bannerFileName}"
						alt={bannerAltText}
						class="header-banner"
						loading="lazy"
					/>
				</picture>
			</div>
		{:else if element === 'slogan' && market?.slogan}
			<div class="header-slogan-container">
				<div class="header-slogan" style={sloganStyles()}>
					{@html market.slogan}
				</div>
			</div>
		{/if}
	{/each}
</header>

<style>
	.modern-header {
		position: relative;
		z-index: 100;
		background-color: var(--lg-bg-primary);
	}

	.header-banner-container {
		width: 100%;
		background-color: var(--lg-bg-primary);
	}

	.header-banner {
		width: 100%;
		height: auto;
		display: block;
		/* Allow the full image to display without cropping */
		object-fit: contain;
		object-position: center;
	}

	.header-slogan-container {
		width: 100%;
		background-color: var(--lg-bg-primary);
		padding: var(--lg-space-4) 0;
	}

	.header-slogan {
		text-align: center;
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 var(--lg-space-4);
		line-height: 1.4;
		/* Default styles - only apply if no inline styles are present */
		font-family: Georgia, serif;
		color: var(--lg-text-primary);
	}
	/* Constrain images in slogan to prevent overflow */
	.header-slogan-container .header-slogan img {
		max-width: 100% !important;
		height: auto;
		display: block;
		margin: 0 auto;
	}

	/* Only set default font-size if no inline style is present */
	.header-slogan:not([style*='font-size']) {
		font-size: var(--lg-text-2xl);
	}

	/* Ensure nested HTML elements inherit styles */
	.header-slogan :global(*) {
		font-family: inherit;
		font-size: inherit;
		color: inherit;
		line-height: inherit;
	}

	.header-slogan :global(p) {
		margin: 0;
		padding: 0;
	}

	.header-slogan :global(strong),
	.header-slogan :global(b) {
		font-weight: bold;
	}

	.header-slogan :global(em),
	.header-slogan :global(i) {
		font-style: italic;
	}
	@keyframes pulse {
		0%,
		100% {
			opacity: 0.9;
		}
		50% {
			opacity: 1;
		}
	}

	@keyframes bounce {
		0%,
		100% {
			transform: translateY(0);
		}
		50% {
			transform: translateY(-2px);
		}
	}

	@media (max-width: 768px) {
		.header-slogan {
			padding: 0 var(--lg-space-3);
		}

		/* Only set mobile font-size if no inline style is present */
		.header-slogan:not([style*='font-size']) {
			font-size: var(--lg-text-xl);
		}
	}
</style>
